<p-toast></p-toast>

<app-page-shell *ngIf="canViewClearance">
    <app-page-hero
        eyebrow="Operations"
        title="Clearance Overview"
        subtitle="Monitor declaration readiness, document exceptions, filing pressure, and release handoffs across active clearance work.">
        <div actions>
            <p-button label="List Clearances" icon="pi pi-list" severity="secondary" [outlined]="true" (click)="openList()"></p-button>
            <p-button *ngIf="canCreateClearance" label="Create Clearance" icon="pi pi-plus" (click)="addClearance()"></p-button>
        </div>
    </app-page-hero>

    <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

    <div class="overview-grid">
        <app-page-data-panel title="Workflow Pipeline" subtitle="Current spread across clearance stages" [resultText]="totalRecords + ' total'">
            <div actions>
                <p-button icon="pi pi-refresh" label="Refresh" severity="secondary" [outlined]="true" (click)="loadDashboard()"></p-button>
            </div>
            <div class="pipeline-list">
                <div class="pipeline-row" *ngFor="let bucket of statusBuckets">
                    <div class="pipeline-copy">
                        <span>{{ bucket.label }}</span>
                        <strong>{{ bucket.value }}</strong>
                    </div>
                    <p-progressBar [value]="bucketPercent(bucket)" [showValue]="false" styleClass="pipeline-bar"></p-progressBar>
                </div>
            </div>
        </app-page-data-panel>

        <app-page-data-panel title="Pre-Clearance Watch" subtitle="Jobs near or past ETA minus 48 hours" [resultText]="preClearanceRisk.length + ' flagged'">
            <div class="compact-list" *ngIf="preClearanceRisk.length; else noPreClearanceRisk">
                <button class="job-row" type="button" *ngFor="let clearance of preClearanceRisk" (click)="openWorkspace(clearance)">
                    <span>
                        <strong>{{ clearance.jobNumber || clearance.clearanceJobNumber || '-' }}</strong>
                        <small>{{ clearance.consignee?.name || 'No consignee' }}</small>
                    </span>
                    <span class="job-meta">
                        <small>{{ clearance.eta | date:'yyyy-MM-dd' }}</small>
                        <p-tag [value]="preClearanceLabel(clearance)" [severity]="isPreClearanceLate(clearance) ? 'danger' : 'warning'"></p-tag>
                    </span>
                </button>
            </div>
            <ng-template #noPreClearanceRisk>
                <div class="empty-state">No pre-clearance deadlines are currently at risk.</div>
            </ng-template>
        </app-page-data-panel>
    </div>

    <div class="overview-grid three">
        <app-page-data-panel title="Document Actions" subtitle="Uploads, reviews, and rejections needing follow-up" [resultText]="documentActionJobs.length + ' jobs'">
            <div class="compact-list" *ngIf="documentActionJobs.length; else noDocumentActions">
                <button class="job-row" type="button" *ngFor="let clearance of documentActionJobs" (click)="openWorkspace(clearance)">
                    <span>
                        <strong>{{ clearance.jobNumber || clearance.clearanceJobNumber || '-' }}</strong>
                        <small>{{ (clearance.shipper && clearance.shipper.shipperName) || clearance.consignee?.name || 'No party' }}</small>
                    </span>
                    <p-tag [value]="clearance.status || '-'" [severity]="getSeverity(clearance.status)"></p-tag>
                </button>
            </div>
            <ng-template #noDocumentActions>
                <div class="empty-state">No document review actions in the latest jobs.</div>
            </ng-template>
        </app-page-data-panel>

        <app-page-data-panel title="Next Arrivals" subtitle="Upcoming ETA sequence for open jobs" [resultText]="nextEtaJobs.length + ' arrivals'">
            <div class="compact-list" *ngIf="nextEtaJobs.length; else noArrivals">
                <button class="job-row" type="button" *ngFor="let clearance of nextEtaJobs" (click)="openWorkspace(clearance)">
                    <span>
                        <strong>{{ clearance.jobNumber || clearance.clearanceJobNumber || '-' }}</strong>
                        <small>{{ clearance.pod || clearance.customsLocation || 'No port' }}</small>
                    </span>
                    <span class="job-meta">
                        <small>{{ clearance.eta | date:'MMM d' }}</small>
                        <p-tag [value]="clearance.status || '-'" [severity]="getSeverity(clearance.status)"></p-tag>
                    </span>
                </button>
            </div>
            <ng-template #noArrivals>
                <div class="empty-state">No upcoming arrivals found in the latest jobs.</div>
            </ng-template>
        </app-page-data-panel>

        <app-page-data-panel title="Exceptions" subtitle="Rejected, cancelled, or late jobs" [resultText]="exceptionJobs.length + ' exceptions'">
            <div class="compact-list" *ngIf="exceptionJobs.length; else noExceptions">
                <button class="job-row" type="button" *ngFor="let clearance of exceptionJobs" (click)="openWorkspace(clearance)">
                    <span>
                        <strong>{{ clearance.jobNumber || clearance.clearanceJobNumber || '-' }}</strong>
                        <small>{{ clearance.consignee?.name || clearance.customsLocation || 'No consignee' }}</small>
                    </span>
                    <p-tag [value]="isPreClearanceLate(clearance) ? 'Late' : (clearance.status || '-')" severity="danger"></p-tag>
                </button>
            </div>
            <ng-template #noExceptions>
                <div class="empty-state">No active clearance exceptions in the latest jobs.</div>
            </ng-template>
        </app-page-data-panel>
    </div>

    <app-page-data-panel title="Recent Clearance Work" subtitle="Latest jobs for quick workspace access" [resultText]="recentJobs.length + ' shown'">
        <p-table [value]="recentJobs" dataKey="clearanceHeaderID" [loading]="isLoading"
            styleClass="p-datatable-gridlines p-datatable-sm" [tableStyle]="{ 'min-width': '64rem' }">
            <ng-template pTemplate="header">
                <tr>
                    <th>Job #</th>
                    <th>Client / Consignee</th>
                    <th>Broker</th>
                    <th>Port</th>
                    <th>ETA</th>
                    <th>Status</th>
                    <th>Action</th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-clearance>
                <tr>
                    <td>
                        <div class="font-bold text-900">{{ clearance.jobNumber || clearance.clearanceJobNumber || '-' }}</div>
                        <div class="text-xs text-500">{{ clearance.bayanNumber || 'No Bayan yet' }}</div>
                    </td>
                    <td>
                        <div class="font-semibold">{{ clearance.consignee?.name || '-' }}</div>
                        <div class="text-xs text-500">{{ clearance.shipper?.shipperName || 'No shipper' }}</div>
                    </td>
                    <td>{{ clearance.broker?.name || '-' }}</td>
                    <td>
                        <div>{{ clearance.pod || clearance.customsLocation || '-' }}</div>
                        <div class="text-xs text-500">{{ clearance.incoterm || 'No incoterm' }}</div>
                    </td>
                    <td>{{ clearance.eta | date:'yyyy-MM-dd' }}</td>
                    <td><p-tag [value]="clearance.status || '-'" [severity]="getSeverity(clearance.status)"></p-tag></td>
                    <td>
                        <p-button icon="pi pi-briefcase" label="Workspace" size="small" [outlined]="true" (click)="openWorkspace(clearance)"></p-button>
                    </td>
                </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
                <tr>
                    <td colspan="7" class="text-center">No clearances found.</td>
                </tr>
            </ng-template>
        </p-table>
    </app-page-data-panel>
</app-page-shell>

<app-page-shell *ngIf="!canViewClearance">
    <app-page-data-panel title="Clearance Overview" subtitle="You do not have permission to view clearance records."></app-page-data-panel>
</app-page-shell>
